import React, { FunctionComponent, useContext } from "react";
import { Button, ButtonProps } from "@cmsgov/ds-medicare-gov";
import { sendLogoutRequest } from "../api";
import { withRouter, RouteComponentProps } from "react-router";
import { useTranslate, logError } from "../helpers";
import { AppStore } from "../app/store";
import { ActionType, MctToastType } from "../@types";
import {
  hasGlobalSession,
  initiateLogin,
  getSlsxSignoutUrl,
} from "../helpers/loginHelpers";
import { useFlags } from "launchdarkly-react-client-sdk";
import { ApiError } from "../helpers/errors";
import {
  AnalyticsActionType,
  AnalyticsButtonStyle,
  AnalyticsButtonType,
} from "../app/contexts/Analytics/types";

interface LoginLogoutButtonProps {
  buttonProps?: Partial<ButtonProps>;
  className?: string;
  loginKey?: string;
  size?: "small" | "big";
  hideIfDisabled?: boolean;
  onLogin?: () => void;
  onClick?: () => void;
  id?: string;
}

const LoginLogoutButton: FunctionComponent<
  LoginLogoutButtonProps & RouteComponentProps
> = ({
  buttonProps = {},
  className,
  history,
  loginKey,
  size,
  hideIfDisabled,
  onLogin,
  onClick,
  id,
}) => {
  const t = useTranslate();
  const { feEnableAuthentication } = useFlags();
  const { state, dispatch } = useContext(AppStore.AppContext);

  const loggedIn = hasGlobalSession(state);

  const logout = async (): Promise<void> => {
    const logoutURL = `/?mctt=${MctToastType.LOGGED_OUT}`;
    const signoutUrl = getSlsxSignoutUrl();
    try {
      await sendLogoutRequest();
    } catch (e) {
      logError("Could not log out user (LoginLogoutButton)", e as ApiError);
    }

    history.push(logoutURL);

    dispatch({ type: ActionType.RESET_STATE });

    if (!state.csr) {
      window.location.href = signoutUrl;
    }
    dispatch({
      type: AnalyticsActionType.SEND_BUTTON_ENGAGEMENT_EVENT,
      settings: {
        button: {
          buttonStyle: AnalyticsButtonStyle.DEFAULT,
          buttonType: AnalyticsButtonType.BUTTON,
          text: buttonText,
        },
        linkUrl: !state.csr ? signoutUrl : logoutURL,
      },
    });
  };

  const login = (): void => {
    if (onClick) {
      onClick();
    }
    initiateLogin(state.language, "", undefined);
    dispatch({
      type: AnalyticsActionType.SEND_BUTTON_ENGAGEMENT_EVENT,
      settings: {
        button: {
          buttonStyle: AnalyticsButtonStyle.DEFAULT,
          buttonType: AnalyticsButtonType.BUTTON,
          text: buttonText,
        },
      },
    });
  };

  const buttonText = loggedIn
    ? t("app.menu.logout")
    : t(loginKey || "app.menu.login");

  const loginDisplay =
    !feEnableAuthentication && !loggedIn ? (
      <p className={className}>{t("app.login_unavailable")}</p>
    ) : (
      <Button
        id={id}
        size={size}
        className={className}
        onClick={loggedIn ? logout : onLogin || login}
        {...buttonProps}
      >
        {buttonText}
      </Button>
    );

  return !feEnableAuthentication && !loggedIn && hideIfDisabled ? (
    <></>
  ) : (
    loginDisplay
  );
};

export default withRouter(LoginLogoutButton);
